import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { ArrowDown, ArrowUp, ArrowUpDown, Crown, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { AgentPerformanceRow } from '@/types/teamPerformance'
import type { DrilldownMetric } from '@/types/analyticsDrilldown'

interface Props {
  agents: AgentPerformanceRow[]
  period: string
  darkMode: boolean
  /** Callback déclenché par clic sur le nom d'un agent — ouvre le panel détaillé.
   *  Distinct des clics sur les chiffres (qui ouvrent les pages drilldown). */
  onAgentClick?: (userId: number) => void
}

// Mapping clé colonne → metric drilldown (8 sur 9 sont drillables).
const COL_TO_METRIC: Partial<Record<keyof AgentPerformanceRow, DrilldownMetric>> = {
  calls_total: 'calls_total',
  calls_inbound_answered: 'calls_inbound',
  calls_outbound: 'calls_outbound',
  calls_missed_rung: 'missed',
  talk_time_seconds: 'talk_time',
  sms_sent: 'sms_sent',
  appointments_created: 'appointments_created',
  voicemails_listened: 'voicemails_listened',
}

type SortKey =
  | 'talk_time_seconds'
  | 'calls_total'
  | 'calls_inbound_answered'
  | 'calls_outbound'
  | 'calls_missed_rung'
  | 'answer_rate_pct'
  | 'sms_sent'
  | 'appointments_created'
  | 'voicemails_listened'

function formatDuration(seconds: number): string {
  if (!seconds) return ''
  const totalMinutes = Math.round(seconds / 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}`
  return `${minutes}m`
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('')
}

export function AgentLeaderboardTable({ agents, period, darkMode, onAgentClick }: Props) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const [sortKey, setSortKey] = useState<SortKey>('talk_time_seconds')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const drilldownLink = (userId: number, col: keyof AgentPerformanceRow): string | null => {
    const metric = COL_TO_METRIC[col]
    if (!metric) return null
    return `/${locale}/manager/analytics/drilldown?metric=${metric}&user_id=${userId}&period=${period}`
  }

  const sorted = useMemo(() => {
    const copy = [...agents]
    copy.sort((a, b) => {
      const av = a[sortKey] ?? 0
      const bv = b[sortKey] ?? 0
      return sortDir === 'desc' ? bv - av : av - bv
    })
    return copy
  }, [agents, sortKey, sortDir])

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const headerCellBase = cn(
    'px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide',
    darkMode ? 'text-slate-400' : 'text-slate-500',
  )
  const bodyCellBase = cn(
    'px-3 py-3 text-sm tabular-nums',
    darkMode ? 'text-slate-200' : 'text-slate-700',
  )
  const rowBorder = darkMode ? 'border-slate-700/60' : 'border-slate-200/70'

  const COLUMNS: { key: SortKey; labelKey: string; align?: 'right' }[] = [
    { key: 'calls_total', labelKey: 'manager.analytics.table.calls', align: 'right' },
    { key: 'calls_inbound_answered', labelKey: 'manager.analytics.table.inbound', align: 'right' },
    { key: 'calls_outbound', labelKey: 'manager.analytics.table.outbound', align: 'right' },
    { key: 'calls_missed_rung', labelKey: 'manager.analytics.table.missed', align: 'right' },
    { key: 'answer_rate_pct', labelKey: 'manager.analytics.table.answerRate', align: 'right' },
    { key: 'talk_time_seconds', labelKey: 'manager.analytics.table.talkTime', align: 'right' },
    { key: 'sms_sent', labelKey: 'manager.analytics.table.sms', align: 'right' },
    { key: 'appointments_created', labelKey: 'manager.analytics.table.appts', align: 'right' },
    { key: 'voicemails_listened', labelKey: 'manager.analytics.table.vm', align: 'right' },
  ]

  return (
    <div
      className={cn(
        'rounded-xl border overflow-hidden',
        darkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-white',
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className={cn(darkMode ? 'bg-slate-800/80' : 'bg-slate-50')}>
            <tr className={cn('border-b', rowBorder)}>
              <th className={cn(headerCellBase, 'pl-4')}>
                {t('manager.analytics.table.agent')}
              </th>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    headerCellBase,
                    col.align === 'right' && 'text-right',
                    'cursor-pointer select-none',
                  )}
                  onClick={() => handleSort(col.key)}
                >
                  <span
                    className={cn(
                      'inline-flex items-center gap-1',
                      col.align === 'right' && 'flex-row-reverse',
                    )}
                  >
                    {sortKey === col.key ? (
                      sortDir === 'desc' ? (
                        <ArrowDown className="h-3 w-3" />
                      ) : (
                        <ArrowUp className="h-3 w-3" />
                      )
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-40" />
                    )}
                    {t(col.labelKey)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((agent) => {
              const prevCalls = agent.calls_total_previous
              const deltaCalls = agent.calls_total - prevCalls
              const isManager = agent.role === 'MANAGER'

              return (
                <tr
                  key={agent.user_id}
                  className={cn(
                    'border-b last:border-b-0 transition-colors',
                    rowBorder,
                    darkMode ? 'hover:bg-slate-800/60' : 'hover:bg-slate-50',
                  )}
                >
                  <td className={cn(bodyCellBase, 'pl-4')}>
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          'h-8 w-8 rounded-full flex items-center justify-center font-semibold text-xs',
                          isManager
                            ? darkMode
                              ? 'bg-amber-500/20 text-amber-300'
                              : 'bg-amber-100 text-amber-700'
                            : darkMode
                              ? 'bg-slate-700 text-slate-200'
                              : 'bg-slate-200 text-slate-700',
                        )}
                      >
                        {agent.avatar_url ? (
                          <img
                            src={agent.avatar_url}
                            alt=""
                            className="h-full w-full rounded-full object-cover"
                          />
                        ) : (
                          getInitials(agent.name) || <User className="h-4 w-4" />
                        )}
                      </div>
                      <div className="flex flex-col">
                        {onAgentClick ? (
                          <button
                            type="button"
                            onClick={() => onAgentClick(agent.user_id)}
                            className={cn(
                              'text-sm font-medium text-left rounded',
                              'hover:underline decoration-dotted underline-offset-2',
                              'focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                              darkMode
                                ? 'text-white hover:text-indigo-300'
                                : 'text-slate-900 hover:text-indigo-600',
                            )}
                          >
                            {agent.name}
                            {isManager && (
                              <Crown
                                className="inline-block h-3 w-3 ml-1 text-amber-500"
                                aria-label={t('manager.analytics.table.managerBadge')}
                              />
                            )}
                          </button>
                        ) : (
                          <span
                            className={cn(
                              'text-sm font-medium',
                              darkMode ? 'text-white' : 'text-slate-900',
                            )}
                          >
                            {agent.name}
                            {isManager && (
                              <Crown
                                className="inline-block h-3 w-3 ml-1 text-amber-500"
                                aria-label={t('manager.analytics.table.managerBadge')}
                              />
                            )}
                          </span>
                        )}
                        {deltaCalls !== 0 && (
                          <span
                            className={cn(
                              'text-xs',
                              deltaCalls > 0
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-rose-600 dark:text-rose-400',
                            )}
                          >
                            {deltaCalls > 0 ? '+' : ''}
                            {deltaCalls}{' '}
                            <span className={cn(darkMode ? 'text-slate-500' : 'text-slate-400')}>
                              {t('manager.analytics.table.vsPrevious')}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <DrillCell
                    value={agent.calls_total}
                    href={drilldownLink(agent.user_id, 'calls_total')}
                    className={cn(bodyCellBase, 'text-right font-semibold')}
                    valueClass={darkMode ? 'text-white' : 'text-slate-900'}
                    darkMode={darkMode}
                  />
                  <DrillCell
                    value={agent.calls_inbound_answered}
                    href={drilldownLink(agent.user_id, 'calls_inbound_answered')}
                    className={cn(bodyCellBase, 'text-right')}
                    darkMode={darkMode}
                  />
                  <DrillCell
                    value={agent.calls_outbound}
                    href={drilldownLink(agent.user_id, 'calls_outbound')}
                    className={cn(bodyCellBase, 'text-right')}
                    darkMode={darkMode}
                  />
                  <DrillCell
                    value={agent.calls_missed_rung}
                    href={drilldownLink(agent.user_id, 'calls_missed_rung')}
                    className={cn(bodyCellBase, 'text-right')}
                    valueClass={
                      agent.calls_missed_rung > 0
                        ? 'text-rose-600 dark:text-rose-400'
                        : undefined
                    }
                    darkMode={darkMode}
                  />
                  {/* answer_rate_pct : non-drillable (ratio dérivé) */}
                  <td className={cn(bodyCellBase, 'text-right')}>
                    {agent.answer_rate_pct.toFixed(1)}%
                  </td>
                  <DrillCell
                    value={formatDuration(agent.talk_time_seconds)}
                    href={drilldownLink(agent.user_id, 'talk_time_seconds')}
                    className={cn(bodyCellBase, 'text-right')}
                    darkMode={darkMode}
                  />
                  <DrillCell
                    value={agent.sms_sent}
                    href={drilldownLink(agent.user_id, 'sms_sent')}
                    className={cn(bodyCellBase, 'text-right')}
                    darkMode={darkMode}
                  />
                  <DrillCell
                    value={agent.appointments_created}
                    href={drilldownLink(agent.user_id, 'appointments_created')}
                    className={cn(bodyCellBase, 'text-right')}
                    darkMode={darkMode}
                  />
                  <DrillCell
                    value={agent.voicemails_listened}
                    href={drilldownLink(agent.user_id, 'voicemails_listened')}
                    className={cn(bodyCellBase, 'text-right')}
                    darkMode={darkMode}
                  />
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Cellule de chiffre cliquable qui drill vers la liste détaillée. */
function DrillCell({
  value,
  href,
  className,
  valueClass,
  darkMode,
}: {
  value: number | string
  href: string | null
  className?: string
  valueClass?: string
  darkMode: boolean
}) {
  if (!href) {
    return (
      <td className={className}>
        <span className={valueClass}>{value}</span>
      </td>
    )
  }
  return (
    <td className={className}>
      <Link
        to={href}
        className={cn(
          'inline-block hover:underline decoration-dotted underline-offset-2',
          'focus:outline-none focus:ring-2 focus:ring-indigo-500/40 rounded',
          darkMode ? 'hover:text-indigo-300' : 'hover:text-indigo-600',
          valueClass,
        )}
      >
        {value}
      </Link>
    </td>
  )
}
